<template>
    <div
        class="relative aspect-video w-full shrink-0 overflow-hidden rounded-8">
        <template v-if="video">
            <video
                playsinline
                preload="auto"
                ref="video"
                pip="false"
                muted
                :src="video"
                class="size-full object-cover" />
            <picture ref="poster">
                <source
                    v-if="posterWebp"
                    type="image/webp"
                    :srcset="posterWebp" />
                <source
                    v-else-if="!posterWebp && !poster"
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />
                <img
                    loading="lazy"
                    :alt="alt"
                    :src="
                        poster ||
                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    :class="{ 'opacity-0': active }"
                    class="absolute inset-0 size-full object-cover transition-opacity duration-300" />
            </picture>
            <PlaySvg
                v-if="showPlay"
                class="absolute bottom-1 left-1 size-6 text-accent transition-all duration-300 group-hover:opacity-0" />
        </template>
        <span
            v-else
            class="relative">
            <picture
                v-if="image || imageWebp"
                :class="{
                    'opacity-100 transition-opacity duration-[250ms] hover:opacity-0':
                        secondImage || secondImageWebp,
                }"
                class="relative z-20 size-full object-cover">
                <source
                    v-if="imageWebp"
                    type="image/webp"
                    :srcset="imageWebp" />
                <source
                    v-else-if="!imageWebp && !image"
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />
                <img
                    loading="lazy"
                    :alt="alt"
                    :src="
                        image ||
                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    :class="{
                        'opacity-100 transition-opacity duration-[250ms] hover:opacity-0':
                            secondImage,
                    }"
                    class="relative z-20 size-full object-cover" />
            </picture>
            <picture
                v-if="secondImage || secondImageWebp"
                class="absolute left-0 top-0 z-10 size-full object-cover">
                <source
                    v-if="secondImageWebp"
                    type="image/webp"
                    :srcset="secondImageWebp" />
                <source
                    v-else-if="!secondImageWebp && !secondImage"
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />
                <img
                    v-if="secondImage"
                    loading="lazy"
                    :alt="alt"
                    :src="
                        secondImage ||
                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    class="absolute left-0 top-0 z-10 size-full object-cover" />
            </picture>
        </span>
    </div>
</template>

<script setup lang="ts">
import PlaySvg from '~/components/base/base-video/PlaySvg.vue'

const videoRef = useTemplateRef('video')
const posterRef = useTemplateRef('poster')

const props = defineProps({
    video: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
    poster: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
    posterWebp: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
    image: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
    imageWebp: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
    secondImage: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
    secondImageWebp: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
    alt: {
        type: String as PropType<string>,
        default: '',
    },
    active: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
    showPlay: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
})

const playVideo = () => {
    if (!posterRef.value || !videoRef.value) return

    videoRef.value.load()
    videoRef.value.loop = true
    videoRef.value.play()
}

const stopVideo = () => {
    if (!posterRef.value || !videoRef.value) return

    videoRef.value.pause()
}

watch(
    () => props.active,
    () => {
        if (props.active) {
            playVideo()
        } else {
            stopVideo()
        }
    }
)
</script>
